export function initReadonlySelectPreviews(root = document) {
  root.querySelectorAll('select[data-readonly-select-preview="1"]').forEach((select) => {
    if (select.dataset.readonlySelectInitialized === "1") return;
    select.dataset.readonlySelectInitialized = "1";
    const initialValues = Array.from(select.selectedOptions, (option) => option.value);

    const restoreSelection = (event) => {
      event.stopImmediatePropagation();
      if (select.tomselect) {
        select.tomselect.setValue(initialValues, true);
      } else {
        Array.from(select.options).forEach((option) => {
          option.selected = initialValues.includes(option.value);
        });
      }
    };

    // Restore before change handlers can update dependent fields or submit the form.
    select.addEventListener("input", restoreSelection, true);
    select.addEventListener("change", restoreSelection, true);
  });
}

export function fieldNrTeczki(root = document, selector = 'input[name="nr_teczki"]') {
  const scope =
    typeof root === "string" ? document : root instanceof Element || root instanceof Document ? root : document;
  const resolvedSelector = typeof root === "string" ? root : selector;
  const inputs = scope.querySelectorAll(resolvedSelector);
  if (inputs.length === 0) {
    return false;
  }

  inputs.forEach((input) => {
    // Prevent double-init (e.g. if called multiple times)
    if (input.dataset.pillsInit === "1") {
      return;
    }
    input.dataset.pillsInit = "1";

    const parseValues = (raw) => {
      return String(raw ?? "")
        .split(";")
        .map((v) => v.trim())
        .filter(Boolean);
    };

    const values = parseValues(input.value);
    const isReadonly = input.readOnly || input.disabled;

    const wrapper = document.createElement("div");
    wrapper.className = "d-flex flex-wrap gap-2 align-items-center";
    wrapper.dataset.nrTeczkiPills = "1";

    const pills = document.createElement("div");
    pills.className = "d-flex flex-wrap gap-2 align-items-center";

    const controls = document.createElement("div");
    controls.className = "input-group fileid-control";
    controls.style.maxWidth = "260px";
    controls.hidden = isReadonly;

    const addInput = document.createElement("input");
    addInput.type = "text";
    addInput.maxLength = 64;
    addInput.autocomplete = "off";
    addInput.placeholder = "Dodaj numer teczki…";
    addInput.className = input.className || "form-control";

    // Move the original input's id to the visible input (so labels keep working)
    if (input.id) {
      addInput.id = input.id;
      input.removeAttribute("id");
    }

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "btn btn-secondary";
    addBtn.textContent = "+";
    addBtn.setAttribute("aria-label", "Dodaj numer teczki");

    controls.appendChild(addInput);
    controls.appendChild(addBtn);

    const render = () => {
      pills.innerHTML = "";

      values.forEach((v) => {
        const pill = document.createElement("span");
        pill.className = "badge text-bg-light border rounded-pill d-inline-flex align-items-center";

        const text = document.createElement("span");
        text.textContent = v;

        pill.appendChild(text);

        if (!isReadonly) {
          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "btn-close ms-2";
          removeBtn.setAttribute("aria-label", `Usuń ${v}`);

          removeBtn.addEventListener("click", () => {
            const idx = values.indexOf(v);
            if (idx >= 0) {
              values.splice(idx, 1);
              syncRaw();
            }
          });

          pill.appendChild(removeBtn);
        }

        pills.appendChild(pill);
      });
    };

    const syncRaw = () => {
      input.value = values.join(";");
      render();
      validateIfRequired();
    };

    const addOne = (raw) => {
      const cleaned = String(raw ?? "").trim();
      if (!cleaned) {
        return;
      }

      values.push(cleaned);
      syncRaw();
    };

    const addMany = (raw) => {
      const parts = String(raw ?? "")
        .split(/[;\n\r]+/g)
        .map((v) => v.trim())
        .filter(Boolean);
      parts.forEach(addOne);
    };

    const validateIfRequired = () => {
      if (!input.required) {
        addInput.setCustomValidity("");
        return;
      }
      if (values.length === 0) {
        addInput.setCustomValidity("To pole jest wymagane.");
      } else {
        addInput.setCustomValidity("");
      }
    };

    const onAdd = () => {
      addMany(addInput.value);
      addInput.value = "";
      validateIfRequired();
      addInput.focus();
    };

    // Ensure we still submit raw value to server
    input.type = "hidden";

    // Rebuild DOM: wrapper replaces the original input position, but keeps the hidden input
    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(pills);
    wrapper.appendChild(controls);
    wrapper.appendChild(input);

    // Events
    if (!isReadonly) {
      addInput.addEventListener("input", () => {
        validateIfRequired();
      });

      addInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onAdd();
          return;
        }
        if (event.key === ";" || event.key === ",") {
          event.preventDefault();
          onAdd();
          return;
        }
        if (event.key === "Backspace" && addInput.value === "" && values.length > 0) {
          // Quick remove last pill
          values.pop();
          syncRaw();
        }
      });

      addInput.addEventListener("paste", (event) => {
        const text = event.clipboardData?.getData("text") ?? "";
        if (!text) {
          return;
        }
        // If paste looks like a list, consume it fully
        if (/[;\n\r]/.test(text)) {
          event.preventDefault();
          addMany(text);
          addInput.value = "";
        }
      });

      addBtn.addEventListener("click", onAdd);
    }

    // Handle required validation on submit
    const form = input.closest("form");
    if (form && !form.dataset.nrTeczkiPillsSubmitHook) {
      form.dataset.nrTeczkiPillsSubmitHook = "1";
      form.addEventListener("submit", (e) => {
        // Validate all pillified nr_teczki inputs inside this form
        const pillInputs = form.querySelectorAll('input[name="nr_teczki"][data-pills-init="1"]');
        let firstInvalidAddInput = null;

        pillInputs.forEach((hidden) => {
          if (!hidden.required) {
            return;
          }
          const host = hidden.closest('[data-nr-teczki-pills="1"]');
          const ui = host?.querySelector('input[type="text"]');
          const vals = parseValues(hidden.value);
          if (ui && !ui.hidden) {
            if (vals.length === 0) {
              ui.setCustomValidity("To pole jest wymagane.");
              firstInvalidAddInput ||= ui;
            } else {
              ui.setCustomValidity("");
            }
          }
        });

        if (firstInvalidAddInput) {
          e.preventDefault();
          firstInvalidAddInput.reportValidity();
          firstInvalidAddInput.focus();
        }
      });
    }

    // Initial sync
    syncRaw();
  });

  return true;
}

const normalizeTargets = (target) => {
  if (Array.isArray(target)) {
    return target.map((item) => String(item)).filter(Boolean);
  }

  if (target === undefined || target === null) {
    return [];
  }

  return [String(target)].filter(Boolean);
};

const visibilityConstraints = new WeakMap();

const conditionMatches = (value, condition) => {
  const normalizedValue = String(value ?? "");

  if (condition === undefined || condition === null) {
    return normalizedValue !== "";
  }

  if (Array.isArray(condition)) {
    return condition.map((item) => String(item)).includes(normalizedValue);
  }

  if (typeof condition !== "object") {
    return normalizedValue === String(condition);
  }

  if (Array.isArray(condition.in)) {
    return condition.in.map((item) => String(item)).includes(normalizedValue);
  }

  if (Array.isArray(condition.notIn)) {
    return !condition.notIn.map((item) => String(item)).includes(normalizedValue);
  }

  if (condition.equals !== undefined) {
    return normalizedValue === String(condition.equals);
  }

  if (condition.notEquals !== undefined) {
    return normalizedValue !== String(condition.notEquals);
  }

  return false;
};

const rememberInitialDisabledState = (input) => {
  if (!input || input.dataset.initialDisabledState !== undefined) {
    return;
  }

  input.dataset.initialDisabledState = input.disabled ? "1" : "0";
};

const restoreInitialDisabledState = (input) => {
  if (!input) {
    return;
  }

  if (input.dataset.initialDisabledState === undefined) {
    rememberInitialDisabledState(input);
  }

  input.disabled = input.dataset.initialDisabledState === "1";
};

const clearFieldValue = (input) => {
  if (!input) {
    return;
  }

  if (input.type === "checkbox" || input.type === "radio") {
    input.checked = false;
  } else {
    input.value = "";
  }

  if (input.type === "date" && input.id) {
    const hidden = document.getElementById(`${input.id}-hidden`);
    if (hidden) {
      hidden.value = "";
    }
  }
};

const rememberInitialRequiredState = (input) => {
  if (!input || input.dataset.initialRequiredState !== undefined) {
    return;
  }

  input.dataset.initialRequiredState = input.required ? "1" : "0";
};

const restoreInitialRequiredState = (input) => {
  if (!input) {
    return;
  }

  if (input.dataset.initialRequiredState === undefined) {
    rememberInitialRequiredState(input);
  }

  input.required = input.dataset.initialRequiredState === "1";
};

const setVisibilityConstraint = (target, constraintKey, visible) => {
  if (!target || !constraintKey) {
    return true;
  }

  let targetConstraints = visibilityConstraints.get(target);
  if (!targetConstraints) {
    targetConstraints = new Map();
    visibilityConstraints.set(target, targetConstraints);
  }

  targetConstraints.set(constraintKey, visible);
  return Array.from(targetConstraints.values()).every(Boolean);
};

const getAutoFillSourceInputs = (element) => {
  const fieldName = element.dataset.fieldRow;
  if (fieldName) {
    const targetedInputs = Array.from(element.querySelectorAll(`[data-autofill-target="${fieldName}"]`));
    if (targetedInputs.length > 0) {
      return targetedInputs;
    }
  }

  return Array.from(element.querySelectorAll("input, select, textarea"));
};

const getAutoFillSourceValue = (inputs) => {
  if (inputs.length === 0) {
    return "";
  }

  const [firstInput] = inputs;

  if (firstInput.type === "radio") {
    return inputs.find((input) => input.checked)?.value ?? "";
  }

  if (firstInput.type === "checkbox") {
    return inputs.filter((input) => input.checked).map((input) => input.value);
  }

  return firstInput.value ?? "";
};

const getAutoFillSourceOptionProperty = (inputs, sourceValue, propertyName) => {
  const normalizedPropertyName = String(propertyName ?? "").trim();
  const [firstInput] = inputs;
  const selectedValue = Array.isArray(sourceValue) ? sourceValue[0] : sourceValue;

  if (!firstInput || normalizedPropertyName === "" || selectedValue === undefined || selectedValue === null) {
    return "";
  }

  const normalizedSelectedValue = String(selectedValue);
  const tomSelectOption = firstInput.tomselect?.options?.[normalizedSelectedValue];
  if (tomSelectOption && Object.prototype.hasOwnProperty.call(tomSelectOption, normalizedPropertyName)) {
    return tomSelectOption[normalizedPropertyName];
  }

  const selectedOption = firstInput.selectedOptions?.[0];
  if (!selectedOption) {
    return "";
  }

  if (Object.prototype.hasOwnProperty.call(selectedOption, normalizedPropertyName)) {
    return selectedOption[normalizedPropertyName];
  }

  const dataAttributeName = `data-${normalizedPropertyName.replaceAll("_", "-")}`;
  return selectedOption.getAttribute(dataAttributeName) ?? "";
};

const setAutoFillTargetValue = (targetInput, value) => {
  if (targetInput.tomselect && typeof targetInput.tomselect.setValue === "function") {
    targetInput.tomselect.setValue(String(value));
    return;
  }

  if (targetInput.type === "radio") {
    targetInput.checked = String(targetInput.value) === String(value);
  } else {
    targetInput.value = value;
  }
  targetInput.dispatchEvent(new Event("change", { bubbles: true }));
};

const applyAutoFillOperation = (sourceElement, sourceInputs, operation, sourceValue, operationIndex) => {
  const targets = normalizeTargets(operation.target);
  if (targets.length === 0) {
    return;
  }

  const scope = sourceElement.closest("form") || document;
  const sourceFieldName = String(
    sourceElement.dataset.fieldRow || sourceElement.getAttribute("data-field-row") || "source",
  );
  const constraintKey = String(operation.constraintKey || `${sourceFieldName}:${operation.operator}:${operationIndex}`);

  targets.forEach((targetName) => {
    const targetRows = Array.from(scope.querySelectorAll(`[data-field-row="${targetName}"]`));
    const targetInputs = Array.from(scope.querySelectorAll(`[data-autofill-target="${targetName}"]`));

    switch (operation.operator) {
      case "valueMap": {
        const rawMap = operation.map ?? {};
        const operationSourceValue = operation.sourceOptionProperty
          ? getAutoFillSourceOptionProperty(sourceInputs, sourceValue, operation.sourceOptionProperty)
          : sourceValue;
        const normalizedSourceValue = Array.isArray(operationSourceValue)
          ? String(operationSourceValue[0] ?? "")
          : String(operationSourceValue ?? "");
        const mappedValue = rawMap[normalizedSourceValue] ?? rawMap[parseInt(normalizedSourceValue, 10)];

        if (mappedValue === undefined) {
          return;
        }

        targetInputs.forEach((targetInput) => {
          if ((operation.preserveTargetValues ?? []).map(String).includes(String(targetInput.value))) return;
          setAutoFillTargetValue(targetInput, mappedValue);
        });
        break;
      }

      case "visibility": {
        const visible = conditionMatches(sourceValue, operation.showWhen);

        targetRows.forEach((row) => {
          const rowVisible = setVisibilityConstraint(row, constraintKey, visible);
          row.classList.toggle("d-none", !rowVisible);
        });

        targetInputs.forEach((targetInput) => {
          rememberInitialDisabledState(targetInput);
          rememberInitialRequiredState(targetInput);

          const inputVisible = setVisibilityConstraint(targetInput, constraintKey, visible);

          if (inputVisible) {
            restoreInitialDisabledState(targetInput);
            restoreInitialRequiredState(targetInput);
          } else {
            if (operation.clearWhenHidden) {
              clearFieldValue(targetInput);
            }

            targetInput.required = false;
            if (operation.disableWhenHidden !== false) {
              targetInput.disabled = true;
            }
          }
        });
        break;
      }

      default:
        break;
    }
  });
};

const initializeAutoFillSource = (element) => {
  const source = element.dataset.autoFill;
  if (!source) {
    return;
  }

  let operations = [];
  try {
    operations = JSON.parse(source);
  } catch (error) {
    return;
  }

  if (!Array.isArray(operations) || operations.length === 0) {
    return;
  }

  const inputs = getAutoFillSourceInputs(element);
  if (inputs.length === 0 || element.dataset.autoFillInit === "1") {
    return;
  }

  element.dataset.autoFillInit = "1";

  const runOperations = (mode = "change") => {
    const sourceValue = getAutoFillSourceValue(inputs);

    operations.forEach((operation, operationIndex) => {
      if (mode === "init" && !operation.applyOnInit) {
        return;
      }

      applyAutoFillOperation(element, inputs, operation, sourceValue, operationIndex);
    });
  };

  inputs.forEach((input) => {
    input.addEventListener("change", () => runOperations("change"));
    input.addEventListener("input", () => runOperations("change"));
  });

  runOperations("init");
};

export function autoFillField() {
  const inputs = Array.from(document.querySelectorAll("[data-auto-fill]"));
  if (inputs.length === 0) {
    return false;
  }

  inputs.forEach((element) => {
    initializeAutoFillSource(element);
  });

  return true;
}

const parseProgressiveFieldGroups = (rawConfig) => {
  let config = null;
  try {
    config = JSON.parse(rawConfig);
  } catch (error) {
    return [];
  }

  const groups = Array.isArray(config) ? config : [config];

  return groups
    .map((group) => {
      if (!group || typeof group !== "object") {
        return [];
      }

      if (Array.isArray(group.fields)) {
        return group.fields.map((field) => String(field)).filter(Boolean);
      }

      const prefix = String(group.prefix ?? "");
      const start = Number.parseInt(group.start ?? 1, 10);
      const max = Number.parseInt(group.max ?? 0, 10);
      if (!prefix || !Number.isFinite(start) || !Number.isFinite(max) || max < start) {
        return [];
      }

      return Array.from({ length: max - start + 1 }, (_, index) => `${prefix}${start + index}`);
    })
    .filter((fields) => fields.length > 0);
};

const getProgressiveInput = (form, fieldName) => {
  const escapedName = window.CSS?.escape ? window.CSS.escape(fieldName) : fieldName.replace(/"/g, '\\"');
  return form.querySelector(`[name="${escapedName}"]`);
};

const getProgressiveVisibleCount = (items) => {
  const lastFilledIndex = items.reduce((lastIndex, item, index) => {
    return String(item.input.value ?? "").trim() !== "" ? index : lastIndex;
  }, -1);

  return Math.min(Math.max(lastFilledIndex + 2, 1), items.length);
};

const createProgressiveRemoveButton = (item, update) => {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn-outline-danger";
  button.innerHTML = '<i class="bi bi-x-lg" aria-hidden="true"></i>';
  button.setAttribute("aria-label", "Usuń pozycję");
  button.setAttribute("title", "Usuń pozycję");

  button.addEventListener("click", () => {
    item.input.value = "";
    item.input.dispatchEvent(new Event("input", { bubbles: true }));
    item.input.dispatchEvent(new Event("change", { bubbles: true }));
    update({ collapse: true });
    item.input.focus();
  });

  return button;
};

const appendProgressiveControls = (form, items, update) => {
  const lastItem = items[items.length - 1];
  const firstInputWrapper = items[0]?.input.parentElement;
  if (!lastItem || !firstInputWrapper || form.querySelector('[data-progressive-fields-controls="1"]')) {
    return null;
  }

  items.forEach((item) => {
    if (item.input.disabled || item.input.readOnly || item.row.querySelector('[data-progressive-field-remove="1"]')) {
      return;
    }

    const inputGroup = document.createElement("div");
    inputGroup.className = "input-group";
    item.input.parentNode.insertBefore(inputGroup, item.input);
    inputGroup.appendChild(item.input);

    const removeButton = createProgressiveRemoveButton(item, update);
    removeButton.dataset.progressiveFieldRemove = "1";
    inputGroup.appendChild(removeButton);
  });

  const controlsRow = document.createElement("div");
  controlsRow.className = "mb-3 row";
  controlsRow.dataset.progressiveFieldsControls = "1";

  const offset = document.createElement("div");
  offset.className = firstInputWrapper.classList.contains("col-sm-8") ? "col-sm-8 offset-sm-4" : "";

  const addButton = document.createElement("button");
  addButton.type = "button";
  addButton.className = "btn btn-outline-secondary btn-sm";
  addButton.dataset.progressiveFieldsAdd = "1";
  addButton.innerHTML = '<i class="bi bi-plus-lg me-1" aria-hidden="true"></i> Dodaj';
  addButton.addEventListener("click", () => update({ add: true }));

  offset.appendChild(addButton);
  controlsRow.appendChild(offset);
  lastItem.row.parentNode.insertBefore(controlsRow, lastItem.row.nextSibling);

  return addButton;
};

const initProgressiveFieldGroup = (form, fields) => {
  const items = fields
    .map((fieldName) => {
      const input = getProgressiveInput(form, fieldName);
      const row = form.querySelector(`[data-field-row="${fieldName}"]`) || input?.closest(".form__row");
      return input && row ? { input, row } : null;
    })
    .filter(Boolean);

  if (items.length === 0) {
    return false;
  }

  let visibleCount = getProgressiveVisibleCount(items);
  let addButton = null;

  const update = ({ add = false, collapse = false } = {}) => {
    const minimumVisibleCount = getProgressiveVisibleCount(items);
    if (add) {
      visibleCount = Math.min(Math.max(visibleCount, minimumVisibleCount) + 1, items.length);
    } else if (collapse) {
      visibleCount = minimumVisibleCount;
    } else {
      visibleCount = Math.min(Math.max(visibleCount, 1), items.length);
    }

    items.forEach((item, index) => {
      item.row.classList.toggle("d-none", index >= visibleCount);
    });

    if (addButton) {
      addButton.hidden = visibleCount >= items.length;
    }
  };

  addButton = appendProgressiveControls(form, items, update);

  items.forEach((item) => {
    if (item.input.dataset.progressiveFieldInit === "1") {
      return;
    }

    item.input.dataset.progressiveFieldInit = "1";
    item.input.addEventListener("input", () => update());
    item.input.addEventListener("change", () => update());
  });

  update();
  return true;
};

export function initProgressiveFields(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  const forms = [
    ...(scope instanceof Element && scope.matches("[data-progressive-fields]") ? [scope] : []),
    ...Array.from(scope.querySelectorAll("[data-progressive-fields]")),
  ];

  if (forms.length === 0) {
    return false;
  }

  let initialized = false;
  forms.forEach((form) => {
    const groups = parseProgressiveFieldGroups(form.dataset.progressiveFields ?? "");
    groups.forEach((fields) => {
      initialized = initProgressiveFieldGroup(form, fields) || initialized;
    });
  });

  return initialized;
}

const zeroNumberFocusScopes = new WeakSet();

const isZeroNumberValue = (value) => {
  const normalizedValue = String(value ?? "").trim();
  if (normalizedValue === "") {
    return false;
  }

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue === 0;
};

export function initZeroNumberInputSelect(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  if (zeroNumberFocusScopes.has(scope)) {
    return false;
  }

  zeroNumberFocusScopes.add(scope);

  scope.addEventListener("focusin", (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input || input.type !== "number" || input.disabled || input.readOnly || !isZeroNumberValue(input.value)) {
      return;
    }

    requestAnimationFrame(() => {
      if (document.activeElement !== input) {
        return;
      }

      try {
        input.select();
      } catch (error) {
        return;
      }
    });
  });

  return true;
}

export function sanitizeFormFields() {
  const inputs = Array.from(document.querySelectorAll("[data-sanitize]"));
  if (inputs.length === 0) {
    return false;
  }

  inputs.forEach((input) => {
    if (input.dataset.sanitizeInit === "1") {
      return;
    }

    let config = null;
    try {
      config = JSON.parse(input.dataset.sanitize);
    } catch (error) {
      return;
    }

    if (!config || typeof config !== "object") {
      return;
    }

    input.dataset.sanitizeInit = "1";

    const applySanitizer = () => {
      const rawValue = String(input.value ?? "");
      let sanitized = rawValue;

      switch (config.operator) {
        case "trim": {
          sanitized = rawValue.trim();
          break;
        }

        case "decimal": {
          sanitized = rawValue.replace(/,/g, ".").replace(/[^0-9.]/g, "");
          const firstDot = sanitized.indexOf(".");
          if (firstDot !== -1) {
            sanitized = sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, "");
          }
          break;
        }

        case "integer": {
          sanitized = rawValue.replace(/\D/g, "");
          break;
        }

        case "postalCode": {
          const digits = rawValue.replace(/\D/g, "").slice(0, 5);
          sanitized = digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
          break;
        }

        default:
          return;
      }

      if (sanitized !== rawValue) {
        input.value = sanitized;
      }
    };

    input.addEventListener("input", applySanitizer);
    input.addEventListener("change", applySanitizer);
    applySanitizer();
  });

  return true;
}

export function cleanEmptyGetForms(root = document) {
  const scope = root instanceof Element ? root : document;
  const forms = Array.from(scope.querySelectorAll('form[data-clean-empty-get="1"]'));
  if (forms.length === 0) {
    return false;
  }

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (form.dataset.cleanEmptyGetInit === "1") {
      return;
    }

    form.dataset.cleanEmptyGetInit = "1";

    form.addEventListener("submit", (event) => {
      const method = String(form.getAttribute("method") ?? "get").toLowerCase();
      if (method !== "get") {
        return;
      }

      // Dynamic modals own their GET navigation. Let the event bubble to
      // dynamicModal.js so filters, search, sorting, and pagination reload
      // their content instead of navigating the whole page.
      if (form.closest(".modal.show")) {
        return;
      }

      event.preventDefault();

      // Public hook: other modules (e.g. mask-aware search) may mutate the form
      // before serialization, or cancel navigation entirely by calling
      // event.preventDefault() on the dispatched CustomEvent.
      const beforeSerialize = new CustomEvent("form:before-serialize", {
        bubbles: true,
        cancelable: true,
        detail: { form, reason: "clean-empty-get" },
      });
      if (!form.dispatchEvent(beforeSerialize)) {
        return;
      }

      const targetUrl = new URL(form.getAttribute("action") || window.location.pathname, window.location.href);
      const formData = new FormData(form);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (typeof value !== "string") {
          continue;
        }

        if (value.trim() === "") {
          continue;
        }

        params.append(key, value);
      }

      targetUrl.search = params.toString();
      window.location.assign(targetUrl.toString());
    });
  });

  return true;
}

export function initAutoSubmitFilterForms(root = document) {
  const scope = root instanceof Element ? root : document;
  const forms = Array.from(scope.querySelectorAll('form[data-auto-submit-filters="1"]'));
  if (forms.length === 0) {
    return false;
  }

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (form.dataset.autoSubmitFiltersInit === "1") {
      return;
    }

    form.dataset.autoSubmitFiltersInit = "1";

    form.addEventListener("change", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) {
        return;
      }

      if (
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLSelectElement ||
          target instanceof HTMLTextAreaElement
        )
      ) {
        return;
      }

      if (target.matches('input[type="text"], input[type="search"], textarea')) {
        return;
      }

      if (target instanceof HTMLSelectElement && target.multiple) {
        return;
      }

      if (target.name === "q") {
        return;
      }

      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
      }
    });
  });

  return true;
}

export function initAdvancedFilterToggles(root = document) {
  const scope = root instanceof Element ? root : document;
  const buttons = Array.from(scope.querySelectorAll("button[data-toggle-filters]"));
  if (buttons.length === 0) {
    return false;
  }

  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    if (button.dataset.toggleFiltersInit === "1") {
      return;
    }

    const form = button.closest("form");
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const panelId = String(button.getAttribute("aria-controls") ?? "").trim();
    const panel =
      (panelId !== "" ? document.getElementById(panelId) : null) || form.querySelector("[data-advanced-filters-panel]");

    if (!(panel instanceof HTMLElement)) {
      return;
    }

    button.dataset.toggleFiltersInit = "1";

    const setExpanded = (isExpanded) => {
      form.classList.toggle("is-advanced-open", isExpanded);
      button.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      button.classList.toggle("active", isExpanded);
      panel.hidden = !isExpanded;
    };

    setExpanded(form.classList.contains("is-advanced-open") || form.dataset.advancedFiltersDefaultOpen === "1");

    button.addEventListener("click", () => {
      setExpanded(!form.classList.contains("is-advanced-open"));
    });
  });

  return true;
}

function updateTableBulkToggleState(form) {
  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const formControls = Array.from(form.elements);

  const formId = String(form.getAttribute("id") || "").trim();
  const actionsEl = formId !== "" ? document.querySelector('[data-table-bulk-actions-for="' + formId + '"]') : null;

  const setBulkActionsVisibility = (isVisible) => {
    if (!(actionsEl instanceof HTMLElement)) {
      return;
    }

    actionsEl.classList.toggle("hidden", !isVisible);
    actionsEl.setAttribute("aria-hidden", isVisible ? "false" : "true");
  };

  const setBulkActionCount = (checkedCount) => {
    if (!(actionsEl instanceof HTMLElement)) {
      return;
    }

    const buttons = Array.from(actionsEl.querySelectorAll("button[data-table-bulk-button-label]"));
    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      const baseLabel = String(button.dataset.tableBulkButtonLabel || "").trim();
      if (baseLabel === "") {
        return;
      }

      button.textContent = checkedCount > 0 ? `${baseLabel} (${checkedCount})` : baseLabel;
    });
  };

  const toggle = formControls.find(
    (control) => control instanceof HTMLInputElement && control.matches('[data-table-bulk-toggle="1"]'),
  );
  if (!(toggle instanceof HTMLInputElement)) {
    return;
  }

  const items = formControls
    .filter((input) => input instanceof HTMLInputElement && !input.disabled)
    .filter((input) => input.matches('[data-table-bulk-item="1"]'));

  if (items.length === 0) {
    toggle.checked = false;
    toggle.indeterminate = false;
    setBulkActionCount(0);
    setBulkActionsVisibility(false);
    return;
  }

  const checkedCount = items.filter((input) => input.checked).length;
  toggle.checked = checkedCount === items.length;
  toggle.indeterminate = checkedCount > 0 && checkedCount < items.length;

  setBulkActionCount(checkedCount);
  setBulkActionsVisibility(checkedCount > 0);
}

export function initTableBulkForms(root = document) {
  const scope = root instanceof Element ? root : document;
  const forms = Array.from(scope.querySelectorAll('form[data-table-bulk-form="1"]'));
  if (forms.length === 0) {
    return false;
  }

  const resolveBulkActionField = (form) => {
    const inlineSelect = form.querySelector('select[name="bulk_action"]');
    if (inlineSelect instanceof HTMLSelectElement) {
      return inlineSelect;
    }

    const inlineInput = form.querySelector('input[name="bulk_action"]');
    if (inlineInput instanceof HTMLInputElement) {
      return inlineInput;
    }

    const formId = String(form.getAttribute("id") || "").trim();
    if (formId === "") {
      return null;
    }

    const linkedSelect = document.querySelector(`select[name="bulk_action"][form="${formId}"]`);
    if (linkedSelect instanceof HTMLSelectElement) {
      return linkedSelect;
    }

    const linkedInput = document.querySelector(`input[name="bulk_action"][form="${formId}"]`);
    return linkedInput instanceof HTMLInputElement ? linkedInput : null;
  };

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (form.dataset.tableBulkInit === "1") {
      updateTableBulkToggleState(form);
      return;
    }

    form.dataset.tableBulkInit = "1";

    form.addEventListener("submit", (event) => {
      const actionField = resolveBulkActionField(form);
      const action =
        actionField instanceof HTMLInputElement || actionField instanceof HTMLSelectElement ? actionField.value : "";
      const checkedItems = Array.from(form.elements)
        .filter((input) => input instanceof HTMLInputElement && input.checked && !input.disabled)
        .filter((input) => input.matches('[data-table-bulk-item="1"]'));

      if (action === "") {
        event.preventDefault();
        return;
      }

      if (checkedItems.length === 0) {
        event.preventDefault();
        return;
      }

      const confirmMessage = String(form.dataset.tableBulkConfirm || "").trim();
      if (confirmMessage !== "" && !window.confirm(confirmMessage)) {
        event.preventDefault();
      }
    });

    const handleSelectionChange = (event) => {
      const target = event.target instanceof HTMLInputElement ? event.target : null;
      if (!target) {
        return;
      }

      if (target.matches('[data-table-bulk-toggle="1"]')) {
        const items = Array.from(form.elements)
          .filter((input) => input instanceof HTMLInputElement && !input.disabled)
          .filter((input) => input.matches('[data-table-bulk-item="1"]'));
        items.forEach((input) => {
          input.checked = target.checked;
        });
        updateTableBulkToggleState(form);
        return;
      }

      if (target.matches('[data-table-bulk-item="1"]')) {
        updateTableBulkToggleState(form);
      }
    };

    Array.from(form.elements)
      .filter(
        (control) =>
          control instanceof HTMLInputElement &&
          (control.matches('[data-table-bulk-toggle="1"]') || control.matches('[data-table-bulk-item="1"]')),
      )
      .forEach((control) => control.addEventListener("change", handleSelectionChange));

    updateTableBulkToggleState(form);
  });

  return true;
}

export function initConfirmForms(root = document) {
  const scope = root instanceof Element ? root : document;
  const forms = Array.from(scope.querySelectorAll("form[data-confirm]"));
  if (forms.length === 0) {
    return false;
  }

  forms.forEach((form) => {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (form.dataset.confirmInit === "1") {
      return;
    }

    form.dataset.confirmInit = "1";

    form.addEventListener("submit", (event) => {
      const confirmMessage = String(form.dataset.confirm || "").trim();
      if (confirmMessage !== "" && !window.confirm(confirmMessage)) {
        event.preventDefault();
      }
    });
  });

  return true;
}

export function inlineSearch() {
  let inputs = Array.from(document.querySelectorAll("[data-inline-search]"));
  if (inputs.length > 0) {
    inputs.forEach((item) => {
      item.addEventListener("input", function () {
        var q = this.value.toLowerCase();
        document.querySelectorAll(".teacher-card, .teacher-assignments-sidebar [data-name]").forEach(function (card) {
          card.style.display = card.dataset.name.indexOf(q) !== -1 ? "" : "none";
        });
      });
    });
  }
}
